// Wires the game together: the H1 simulation on its clock, the island map and a tile's interior with
// the transition between them, the renderer, gestures, the HUD and the performance meter.
import islandData from './data/island.json';
import { Game, PUMP_MS } from './game';
import { bindGestures, type ScreenPoint } from './input/gestures';
import { FrameScheduler } from './loop/frame-scheduler';
import { PerfMonitor } from './loop/perf-monitor';
import type { CameraMode, CameraRig, Rect } from './render/camera-rig';
import { frameBufferBytes, geometryBytes } from './render/memory';
import { Stage, type Resolution } from './render/stage';
import type { Report } from './sim/report';
import { h } from './ui/dom';
import { Hud, type TimeChoice } from './ui/hud';
import { ICONS } from './ui/icons';
import { Notices } from './ui/notice';
import { PerfPanel } from './ui/perf-panel';
import { safeLocalStorage, SettingsStore, type Settings } from './ui/settings';
import { InteriorView } from './views/interior-view';
import { MapView } from './views/map-view';
import { diveTowards, flightAt, riseFrom, type Flight } from './views/transition';
import { parseIsland } from './world/island';

export type ViewName = 'map' | 'interior' | 'transition';

/** Hook for automated tests and for poking at the build from the browser console. */
export interface IsloraDebug {
  readonly ready: boolean;
  readonly frames: number;
  readonly mode: string;
  readonly settings: Settings;
  stats(): { calls: number; triangles: number; objects: number; vehicles: number };
  camera(): { x: number; z: number; distance: number; mode: CameraMode };
  /** Which screen is showing. */
  view(): ViewName;
  /** Screen point of a map tile's centre. */
  tileScreen(id: string): ScreenPoint | undefined;
  /** Screen point of a point of the open interior, in cell units ((0, 0) is the first cell's outer corner). */
  cellScreen(x: number, y: number): ScreenPoint | undefined;
  /** Narrowest side of a cell of the open interior on screen, in CSS pixels. */
  cellPixels(): number | undefined;
  /** Runs game time straight away. */
  advance(ms: number): void;
  report(): Report;
}

declare global {
  interface Window {
    __islora?: IsloraDebug;
  }
}

interface PerformanceWithMemory extends Performance {
  readonly memory?: { readonly usedJSHeapSize: number };
}

function webglAvailable(): boolean {
  try {
    return !!document.createElement('canvas').getContext('webgl2');
  } catch {
    return false;
  }
}

export interface App {
  readonly notices: Notices;
  readonly settings: Settings;
}

/** How often the open panels and the interior's figures are refreshed when no frame is drawn, in ms. */
const FIGURES_MS = 250;

export function startApp(root: HTMLElement): App | undefined {
  const store = new SettingsStore(location.search, safeLocalStorage());

  if (!webglAvailable()) {
    root.append(h('div', { class: 'fatal' }, h('p', {}, 'Este navegador no puede mostrar la isla en 3D.'), h('p', {}, 'Abre Islora en Safari o Chrome actualizados.')));
    return undefined;
  }

  const canvas = h('canvas', { class: 'scene', 'aria-label': 'Mapa de la isla' });
  const labelRoot = h('div', { class: 'labels', 'aria-hidden': 'false' });
  const fade = h('div', { class: 'fade', 'aria-hidden': 'true' });
  root.append(canvas, labelRoot, fade);
  const notices = new Notices(root);

  const game = new Game({ frozen: store.current.shot });
  const island = parseIsland(islandData);
  const monitor = new PerfMonitor();

  let view: 'map' | 'interior' = 'map';
  let flight: (Flight & { readonly tile: string }) | undefined;
  let interior: InteriorView | undefined;

  const invalidate = (): void => {
    scheduler.invalidate();
  };
  const map = new MapView(island, labelRoot, game, { stress: store.current.stress, shadows: store.current.shadows, camera: store.current.camera }, { onEnter: (tile) => { enter(tile); }, invalidate });

  let hideLostNotice: (() => void) | undefined;
  const stage = new Stage({
    canvas,
    onContextLost: () => {
      hideLostNotice = notices.show({ text: 'Recuperando la isla…', tone: 'amber', icon: ICONS.arrowsClockwise });
    },
    onContextRestored: () => {
      hideLostNotice?.();
      scheduler.invalidate();
    },
  });
  stage.setShadows(store.current.shadows);
  stage.setResolution(store.current.resolution);

  const activeRig = (): CameraRig => (view === 'interior' && interior ? interior.rig : map.rig);
  const activeScene = (): InteriorView['scene']['scene'] => (view === 'interior' && interior ? interior.scene.scene : map.world.scene);

  let clock = 0;
  let readyMs: number | undefined;
  let gpuBytes = geometryBytes(map.world.scene);
  const scheduler = new FrameScheduler({
    requestFrame: (cb) => requestAnimationFrame(cb),
    gpuBusy: () => stage.gpuBusy,
    render: (time, delta) => {
      const start = performance.now();
      if (game.pump(time) > 0) refreshFigures();
      if (flight) flyFrame(time);
      else scheduler.setActive('camera', activeRig().tick(time, delta));
      if (view === 'map' && store.current.animations) {
        // Clamp so a long pause (tab hidden) does not make vehicles jump.
        clock += Math.min(delta, 100) / 1000;
        map.world.update(clock);
      }
      if (view === 'interior' && interior) interior.frame(time);
      stage.render(activeScene(), activeRig().camera);
      if (view === 'map') map.frame();
      monitor.record(time, performance.now() - start);
      // Ready once a frame has been drawn with the web font in place (labels measured with it).
      if (fontsLoaded) ready = true;
      if (readyMs === undefined) {
        readyMs = performance.now();
        void document.fonts.ready.then(() => {
          fontsLoaded = true;
          map.labels.remeasure();
          scheduler.invalidate();
          // Once the map is on screen, get the workshop's interior ready in the background.
          window.setTimeout(() => {
            const first = [...game.sim.world.tiles.values()].find((t) => t.interior && island.tiles.some((m) => m.id === t.def.id));
            if (first && !interior) prepare(first.def.id);
          }, 0);
        });
      }
    },
  });
  let ready = false;
  let fontsLoaded = false;

  const resize = (): void => {
    const rect = root.getBoundingClientRect();
    stage.resize(rect.width, rect.height);
    map.resize(rect.width, rect.height);
    if (interior && view === 'interior') interior.resize(rect.width, rect.height, rect, sharedObstacles(rect));
    scheduler.invalidate();
  };
  /** The top-right column (time, camera, meter) over the interior, relative to the root. */
  const sharedObstacles = (origin: DOMRect): Rect[] => {
    const r = hud.column.getBoundingClientRect();
    return r.width > 0 ? [{ left: r.left - origin.left, top: r.top - origin.top, right: r.right - origin.left, bottom: r.bottom - origin.top }] : [];
  };
  new ResizeObserver(resize).observe(root);
  resize();

  // ------------------------------------------------------- transition ---

  /**
   * Builds a tile's interior ahead of time and compiles its shaders in the background, so the first
   * dive into it does not stall on them.
   */
  function prepare(tile: string): InteriorView {
    if (interior?.session.tile === tile) return interior;
    interior?.dispose();
    interior = new InteriorView(
      game,
      tile,
      { shadows: store.current.shadows, camera: store.current.camera },
      {
        onBack: () => {
          leave();
        },
        invalidate,
        invalidateShadows: () => {
          stage.invalidateShadows();
        },
      },
    );
    root.insertBefore(interior.labels.el, fade);
    root.insertBefore(interior.hud.el, hud.el);
    root.append(interior.card.el);
    void stage.renderer.compileAsync(interior.scene.scene, interior.rig.camera).catch(() => {
      // Compiling ahead is only a head start: the first frame compiles whatever is missing.
    });
    return interior;
  }

  function enter(tile: string): void {
    if (flight || view !== 'map') return;
    const centre = map.tileCentre(tile);
    if (!centre) return;
    map.closePanel();
    const inside = prepare(tile);
    // The time controls come in with the dive; the interior's camera frames around them and the HUD,
    // laid out unseen for a moment so their sizes are known before anything shows.
    hud.showTime(true, timeChoice());
    inside.hud.el.hidden = false;
    inside.hud.el.classList.add('is-measuring');
    const rect = root.getBoundingClientRect();
    inside.resize(rect.width, rect.height, rect, sharedObstacles(rect));
    inside.hud.el.classList.remove('is-measuring');
    inside.hud.el.hidden = true;
    inside.rig.setMode(store.current.camera);
    inside.rig.reset();
    // A flick or a slide still running on the map would carry on after coming back.
    map.rig.halt();
    const mapPose = map.rig.pose;
    const rest = inside.rig.pose;
    flight = {
      tile,
      direction: 'enter',
      start: performance.now(),
      leaveFrom: mapPose,
      leaveTo: diveTowards(mapPose, centre.x, centre.y, centre.z, rest.pitch, 0.35),
      arriveFrom: riseFrom(rest, mapPose.pitch, 1.6),
      arriveTo: rest,
    };
    hud.el.classList.add('is-flying');
    scheduler.setActive('flight', true);
  }

  function leave(): void {
    if (flight || view !== 'interior' || !interior) return;
    const tile = interior.session.tile;
    const centre = map.tileCentre(tile);
    if (!centre) return;
    interior.putDown();
    interior.setVisible(false);
    hud.showTime(false, timeChoice());
    interior.rig.halt();
    const from = interior.rig.pose;
    const mapPose = map.rig.pose;
    flight = {
      tile,
      direction: 'leave',
      start: performance.now(),
      leaveFrom: from,
      leaveTo: riseFrom(from, mapPose.pitch, 1.6),
      arriveFrom: diveTowards(mapPose, centre.x, centre.y, centre.z, from.pitch, 0.35),
      arriveTo: mapPose,
    };
    hud.el.classList.add('is-flying');
    scheduler.setActive('flight', true);
  }

  function flyFrame(time: number): void {
    if (!flight || !interior) return;
    const f = flightAt(flight, time);
    const entering = flight.direction === 'enter';
    const showInterior = entering ? f.stage === 'arrive' : f.stage === 'leave';
    if (showInterior !== (view === 'interior')) switchTo(showInterior ? 'interior' : 'map');
    const rig = activeRig();
    rig.place(f.pose);
    const { width, height } = stage.size;
    const centre = showInterior ? { x: 0, y: 0, z: 0 } : (map.tileCentre(flight.tile) ?? { x: 0, y: 0, z: 0 });
    const p = rig.project(centre.x, centre.y, centre.z, width, height);
    const radius = Math.min(width, height);
    fade.style.opacity = f.fade.toFixed(3);
    fade.style.background = `radial-gradient(circle at ${p.x.toFixed(0)}px ${p.y.toFixed(0)}px, transparent ${(radius * 0.22).toFixed(0)}px, var(--sky) ${(radius * 0.48).toFixed(0)}px)`;
    if (f.done) {
      flight = undefined;
      fade.style.opacity = '0';
      rig.restore();
      hud.el.classList.remove('is-flying');
      scheduler.setActive('flight', false);
      if (view === 'interior') {
        interior.setVisible(true);
        hud.showTime(true, timeChoice());
      }
      syncAmbient();
    }
  }

  function switchTo(next: 'map' | 'interior'): void {
    view = next;
    canvas.setAttribute('aria-label', next === 'interior' ? 'Interior de la casilla' : 'Mapa de la isla');
    labelRoot.hidden = next !== 'map';
    gpuBytes = geometryBytes(activeScene());
    stage.invalidateShadows();
    if (next === 'interior' && interior) interior.frame(performance.now());
  }

  // ------------------------------------------------------------ input ---

  const size = (): { width: number; height: number } => stage.size;
  /** A finger or the mouse is down; `gesturing` says whether this gesture steers the view (not during a flight). */
  let pressed = false;
  let gesturing = false;
  bindGestures(canvas, {
    onStart: () => {
      pressed = true;
      gesturing = !flight;
      if (!gesturing) return;
      if (view === 'interior') interior?.beginGesture();
      else map.beginGesture();
      scheduler.setActive('gesture', true);
    },
    oneFinger: (p) => (gesturing && view === 'interior' && interior ? interior.oneFinger(p) : 'pan'),
    onTap: (p) => {
      if (!gesturing) return;
      if (view === 'interior') interior?.tap(p);
      else map.tap(p);
    },
    onDrawStart: (p) => {
      if (gesturing && view === 'interior') interior?.drawStart(p);
    },
    onDraw: (p) => {
      if (gesturing && view === 'interior') interior?.draw(p);
    },
    onDrawEnd: () => {
      if (gesturing && view === 'interior') interior?.drawEnd();
    },
    onDrawCancel: () => {
      if (gesturing && view === 'interior') interior?.drawCancel();
    },
    onPan: (from, to) => {
      if (!gesturing) return;
      if (view === 'interior') interior?.pan(from, to, performance.now());
      else {
        const { width, height } = size();
        map.rig.pan(from, to, width, height, performance.now());
      }
      scheduler.invalidate();
    },
    onZoom: (factor, at) => {
      // The wheel zooms on its own, outside any gesture; a pinch only if its gesture steers the view.
      if (flight || (pressed && !gesturing)) return;
      if (view === 'interior') interior?.zoom(factor, at);
      else map.zoom(factor, at);
      scheduler.invalidate();
    },
    onEnd: () => {
      if (gesturing) {
        if (view === 'interior') interior?.endGesture(performance.now());
        else map.endGesture(performance.now());
      }
      pressed = false;
      gesturing = false;
      scheduler.setActive('gesture', false);
      scheduler.setActive('camera', activeRig().moving);
    },
  });

  // -------------------------------------------------------------- HUD ---

  const timeChoice = (): TimeChoice => (game.sim.clock.paused ? 'pause' : (String(game.sim.clock.speed) as TimeChoice));
  const perfVisible = (): boolean => perfPanel.el.hidden !== true;
  const setPerfVisible = (visible: boolean): void => {
    perfPanel.el.hidden = !visible;
    hud.meter.set(visible);
    store.update({ perf: visible });
    refreshPerf();
  };
  const hud = new Hud(store.current.camera, store.current.perf, {
    onCamera: (mode) => {
      const now = performance.now();
      map.rig.setMode(mode, now);
      interior?.setMode(mode, now);
      store.update({ camera: mode });
      scheduler.setActive('camera', true);
    },
    onMeterToggle: () => {
      setPerfVisible(!perfVisible());
    },
    onTime: (choice) => {
      if (choice === 'pause') game.sim.clock.setPaused(true);
      else game.sim.clock.setSpeed(Number(choice) as 1 | 2 | 4);
      hud.showTime(view === 'interior', timeChoice());
      refreshFigures();
    },
  });
  const perfPanel = new PerfPanel(store.current, {
    onStress: (on) => {
      map.world.setStress(on);
      map.world.update(clock);
      stage.invalidateShadows();
      gpuBytes = geometryBytes(activeScene());
      store.update({ stress: on });
      scheduler.invalidate();
    },
    onAnimations: (on) => {
      store.update({ animations: on });
      syncAmbient();
      scheduler.invalidate();
    },
    onShadows: (on) => {
      map.world.setShadows(on);
      interior?.scene.setShadows(on);
      stage.setShadows(on);
      store.update({ shadows: on });
      scheduler.invalidate();
    },
    onResolution: (value: Resolution) => {
      stage.setResolution(value);
      store.update({ resolution: value });
      scheduler.invalidate();
    },
    onClose: () => {
      setPerfVisible(false);
    },
  });
  perfPanel.el.hidden = !store.current.perf;
  root.append(hud.el, map.panel.el, perfPanel.el);

  window.addEventListener('keydown', (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === 'p') setPerfVisible(!perfVisible());
    if (e.key === 'Escape' && view === 'interior' && interior) {
      interior.cancel();
    }
  });

  function refreshPerf(): void {
    if (!perfVisible()) return;
    const now = performance.now();
    const stats = stage.stats;
    const heap = (performance as PerformanceWithMemory).memory?.usedJSHeapSize;
    const { width, height } = stage.size;
    const sun = view === 'interior' && interior ? interior.scene.sun : map.world.sun;
    perfPanel.update({
      fps: monitor.fps(now),
      workMs: monitor.workMs(now),
      worstMs: monitor.worstIntervalMs(now),
      calls: stats.calls,
      triangles: stats.triangles,
      heapMb: heap === undefined ? undefined : heap / 1048576,
      gpuMb: (gpuBytes + frameBufferBytes(width, height, stage.pixelRatio, store.current.shadows ? sun.shadow.mapSize.x : undefined)) / 1048576,
      frames: scheduler.frames,
      mode: scheduler.mode,
      objects: objectCount(),
      pixels: `${Math.round(width * stage.pixelRatio)} × ${Math.round(height * stage.pixelRatio)}`,
      readyMs,
    });
  }
  // The meter is plain DOM: refreshing it twice a second never triggers a WebGL frame.
  window.setInterval(refreshPerf, 500);

  function objectCount(): number {
    if (view === 'interior' && interior) {
      const c = interior.scene.objectCounts;
      return c.machines + c.belts + c.items + c.decor;
    }
    const counts = map.world.objectCounts;
    return Object.values(counts.decor).reduce((a, b) => a + b, 0) + counts.vehicles;
  }

  /** The figures moved (a tick, a command, the clock): refresh what shows them, and decide on frames. */
  function refreshFigures(): void {
    map.refreshPanel();
    if (view === 'interior' && interior && !flight) {
      interior.frame(performance.now());
      hud.showTime(true, timeChoice());
    }
    syncAmbient();
  }

  // ------------------------------------------------------- lifecycle ---

  function syncAmbient(): void {
    const awake = store.current.animations && !document.hidden;
    scheduler.setAmbient(awake && (view === 'map' || !!interior?.animating));
  }
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) game.rest();
    syncAmbient();
  });
  // The clock is fed even when nothing is drawn, so the factory keeps working on the map.
  if (!store.current.shot) {
    window.setInterval(() => {
      if (document.hidden) return;
      if (game.pump(performance.now()) > 0 && scheduler.mode === 'idle') refreshFigures();
    }, PUMP_MS);
  }
  window.setInterval(() => {
    if (scheduler.mode === 'idle') map.refreshPanel();
  }, FIGURES_MS);
  syncAmbient();
  scheduler.invalidate();

  window.__islora = {
    get ready() {
      return ready;
    },
    get frames() {
      return scheduler.frames;
    },
    get mode() {
      return scheduler.mode;
    },
    get settings() {
      return store.current;
    },
    stats: () => {
      const counts = map.world.objectCounts;
      return {
        calls: stage.stats.calls,
        triangles: stage.stats.triangles,
        objects: view === 'interior' ? objectCount() : Object.values(counts.decor).reduce((a, b) => a + b, 0),
        vehicles: counts.vehicles,
      };
    },
    camera: () => {
      const rig = activeRig();
      return { x: rig.target.x, z: rig.target.z, distance: rig.distance, mode: rig.mode };
    },
    view: () => (flight ? 'transition' : view),
    tileScreen: (id) => map.tileScreen(id),
    cellScreen: (x, y) => (view === 'interior' && interior ? interior.screenOf(x, y) : undefined),
    cellPixels: () => (view === 'interior' && interior ? interior.cellPixels() : undefined),
    advance: (ms) => {
      game.advance(ms);
      refreshFigures();
      scheduler.invalidate();
    },
    report: () => game.report(),
  };

  return { notices, settings: store.current };
}
